import { v4 as uuidv4 } from 'uuid'
import Meta from './Meta'
import store from './store/index'
import { ILibreModule } from '@/contracts/modules/libre/ILibreModule'
import { IModuleMeta } from '@/contracts/core/module/interface/IModuleMeta'
import { IStore } from '@/contracts/modules/libre/stores/IStore'
import { ILibreNode } from '@/contracts/modules/libre/entities/ILibreNode'
import { ILibrePlace } from '@/contracts/modules/libre/entities/ILibrePlace'
import { ILibreData, LibreDataPatch } from '@/contracts/modules/libre/entities/ILibreData'
import { ILibreFilter } from '@/contracts/modules/libre/entities/ILibreFilter'
import { ILibreFound, ILibreTagGroup } from '@/contracts/modules/libre/entities/ILibreFound'
import { ILibreTree } from '@/contracts/modules/libre/use-case/ILibreTree'
import { EnumLibreSet } from '@/contracts/modules/libre/enum/EnumLibreSet'
import { EnumLibreEvent } from '@/contracts/modules/libre/enum/EnumLibreEvent'
import { EnumLibreError } from '@/contracts/modules/libre/enum/EnumLibreError'
import { EnumLibrePart } from '@/contracts/modules/libre/enum/EnumLibrePart'
import { EnumLibreKind, isStructureKind } from '@/contracts/modules/libre/enum/EnumLibreKind'
import { EnumOperationKind } from '@/contracts/system-modules/sync/enum/EnumOperationKind'
import { EnumSystemEvent } from '@/contracts/core/event/enum/EnumSystemEvent'
import { EnumUserKeyError } from '@/contracts/system-modules/auth/key/EnumUserKeyError'
import { EnumConsoleType } from '@/contracts/system-modules/console/enum/EnumConsoleType'
import { IOperation } from '@/contracts/system-modules/sync/operation/IOperation'
import { IUserKey } from '@/contracts/system-modules/auth/key/IUserKey'
import { ILibreCipher } from '@/contracts/modules/libre/cipher/ILibreCipher'
import { IDecryptNode } from '@/contracts/modules/libre/use-case/IDecryptNode'
import { SysConsoleProviderModule } from '@/system-modules/console/ConsoleProvider'
import { sys } from '@/system/SystemProvider'
import { positionAfter, positionBetween } from '@/helpers/positionBetween'
import { LibreNode } from '@/modules/libre/v1/entities/LibreNode'
import { LibreCipher } from '@/modules/libre/v1/cipher/LibreCipher'
import { FoldNode } from '@/modules/libre/v1/use-case/FoldNode'
import { DecryptNode } from '@/modules/libre/v1/use-case/DecryptNode'
import { LibreTree } from '@/modules/libre/v1/use-case/LibreTree'
import { SearchNodes } from '@/modules/libre/v1/use-case/SearchNodes'
import { TagView } from '@/modules/libre/v1/use-case/TagView'
import { ILibreIndexRow, readLibreIndex, writeLibreIndexRow } from '@/modules/libre/v1/storage/LibreIndex'

/** Предел открытых данных одного узла */
export const NODE_LIMIT_BYTES = 256 * 1024
/** Как часто проверяется, ушла ли очередь на узел */
export const PENDING_CHECK_MS = 5000

/** Сегодня в виде YYYY-MM-DD по часам устройства */
export function today(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

class LibreModuleV1 implements ILibreModule {
  private static instance: LibreModuleV1
  private store: IStore = store()
  private userUuid = ''
  private starting: Promise<void> | null = null
  private pendingTimer: ReturnType<typeof setInterval> | null = null
  private foldNode = new FoldNode()
  private cipher: ILibreCipher = new LibreCipher()
  private decryptNode: IDecryptNode = new DecryptNode()
  private searchNodes = new SearchNodes()
  private tagViewCase = new TagView()
  /** Ключи узлов после unwrap: unwrap делается один раз на узел */
  private keys = new Map<string, CryptoKey>()
  // Шифрование асинхронное, а события одного узла должны уходить в порядке вызовов:
  // запись каждого узла идёт своей цепочкой обещаний
  private writing = new Map<string, Promise<unknown>>()
  private writesInFlight = 0
  /** Номер последнего обновления узла: обновление, начатое раньше и закончившееся позже, отбрасывается */
  private refreshing = new Map<string, number>()
  /** Дерево на версию store: пересобирается, когда версия изменилась */
  private tree: LibreTree | null = null
  private treeVersion = -1

  static getInstance(): LibreModuleV1 {
    if (!LibreModuleV1.instance) {
      LibreModuleV1.instance = new LibreModuleV1()
    }
    return LibreModuleV1.instance
  }

  health(): boolean {
    return true
  }

  init(): boolean {
    sys().events().on(EnumSystemEvent.LOCKED, () => this.stop())
    sys().events().on(EnumSystemEvent.UNLOCKED, () => { this.start() })
    this.start()
    return true
  }

  destroy(): boolean {
    this.stop()
    return true
  }

  getMeta(): IModuleMeta {
    return new Meta()
  }

  getViewPath(): string {
    return '/libre/v1/view/index.vue'
  }

  start(): Promise<void> {
    const userUuid = sys().auth.getUserUuid()
    if (userUuid === '' || !this.hasUserKey()) {
      return Promise.resolve()
    }
    if (this.starting !== null && this.userUuid === userUuid) {
      return this.starting
    }
    this.reset()
    this.userUuid = userUuid
    sys().sync.subscribe(EnumLibreSet.NODES, (operation) => this.onOperation(operation))
    this.pendingTimer = setInterval(() => this.checkPending(), PENDING_CHECK_MS)
    this.starting = this.load(userUuid)
    return this.starting
  }

  hasUserKey(): boolean {
    return sys().auth.getUserKey() !== null
  }

  async createNode(parent: string | null, kind: EnumLibreKind, content = ''): Promise<string> {
    await this.start()
    const userKey = this.requireUserKey()
    if (parent !== null) {
      const parentNode = this.requireNode(parent)
      if (!isStructureKind(parentNode.getKind())) {
        throw new Error(EnumLibreError.LEAF_HAS_NO_CHILDREN)
      }
    }
    const uuid = uuidv4()
    const place: ILibrePlace = { parent, position: positionAfter(this.getTree().children(parent).map((node) => node.getPosition())) }
    const data: ILibreData = { kind, tags: [], content, attrs: {}, actualFrom: today(), actualTo: null }
    this.checkLimit(data)
    const nodeKey = await this.cipher.createKey()
    const wrapped = await userKey.wrap(nodeKey)
    this.keys.set(uuid, await userKey.unwrap(wrapped))
    // узел виден сразу, до записи и свёртки: следующий вызов может создавать в нём детей
    this.store.nodes = { ...this.store.nodes, [uuid]: new LibreNode(uuid, place, data, false, null) }
    this.store.version++
    this.markPending(uuid)
    this.writeNode(uuid, EnumLibreEvent.CREATED, async () => ({
      key: wrapped,
      place: await this.encrypt(uuid, EnumLibrePart.PLACE, JSON.stringify(place)),
      data: await this.encrypt(uuid, EnumLibrePart.DATA, JSON.stringify(data)),
    }))
    return uuid
  }

  move(uuid: string, parent: string | null): void {
    this.requireUserKey()
    this.requireNode(uuid)
    if (parent !== null) {
      const target = this.requireNode(parent)
      if (!isStructureKind(target.getKind())) {
        throw new Error(EnumLibreError.LEAF_HAS_NO_CHILDREN)
      }
      if (parent === uuid || this.getTree().ancestors(parent).some((node) => node.getUuid() === uuid)) {
        throw new Error(EnumLibreError.MOVE_INTO_ITSELF)
      }
    }
    const siblings = this.getTree().children(parent).filter((node) => node.getUuid() !== uuid)
    this.writePlace(uuid, { parent, position: positionAfter(siblings.map((node) => node.getPosition())) })
  }

  shift(uuid: string, step: -1 | 1): void {
    this.requireUserKey()
    const node = this.requireNode(uuid)
    const siblings = this.getTree().children(node.getParent())
    const index = siblings.findIndex((item) => item.getUuid() === uuid)
    const target = index + step
    if (index === -1 || target < 0 || target >= siblings.length) {
      return
    }
    const before = step < 0 ? siblings[target - 1] ?? null : siblings[target]
    const after = step < 0 ? siblings[target] : siblings[target + 1] ?? null
    this.writePlace(uuid, { parent: node.getParent(), position: positionBetween(before?.getPosition() ?? null, after?.getPosition() ?? null) })
  }

  changeData(uuid: string, patch: LibreDataPatch): void {
    this.requireUserKey()
    const node = this.requireNode(uuid)
    const data: ILibreData = { ...(node as LibreNode).getData(), ...patch }
    data.tags = [...new Set(data.tags.map((tag) => tag.trim()).filter((tag) => tag !== ''))]
    this.checkLimit(data)
    this.markPending(uuid)
    this.writeNode(uuid, EnumLibreEvent.CHANGED, async () => ({ data: await this.encrypt(uuid, EnumLibrePart.DATA, JSON.stringify(data)) }))
  }

  remove(uuid: string): void {
    this.requireNode(uuid)
    this.markPending(uuid)
    this.writeNode(uuid, EnumLibreEvent.REMOVED, async () => ({}))
  }

  restore(uuid: string): void {
    this.requireNode(uuid)
    this.markPending(uuid)
    this.writeNode(uuid, EnumLibreEvent.RESTORED, async () => ({}))
  }

  getNode(uuid: string): ILibreNode | null {
    if (!this.hasUserKey()) {
      return null
    }
    return this.store.nodes[uuid] ?? null
  }

  getTree(): ILibreTree {
    if (!this.hasUserKey()) {
      return new LibreTree([])
    }
    if (this.tree === null || this.treeVersion !== this.store.version) {
      this.tree = new LibreTree(Object.values(this.store.nodes))
      this.treeVersion = this.store.version
    }
    return this.tree
  }

  getPath(uuid: string): ILibreNode[] {
    return this.getTree().ancestors(uuid)
  }

  search(filter: ILibreFilter): ILibreFound[] {
    return this.searchNodes.exec(this.getTree(), filter)
  }

  tagView(tag: string): ILibreTagGroup[] {
    return this.tagViewCase.exec(this.getTree(), tag)
  }

  getAllTags(): string[] {
    const tree = this.getTree()
    const tags = new Set<string>()
    tree.all().forEach((node) => tree.effectiveTags(node.getUuid()).forEach((tag) => tags.add(tag)))
    return [...tags].sort()
  }

  select(uuid: string | null): void {
    this.store.selected = uuid
  }

  getSelected(): string | null {
    return this.store.selected
  }

  isPending(uuid: string): boolean {
    return this.store.pending.includes(uuid)
  }

  private stop(): void {
    if (this.pendingTimer !== null) {
      clearInterval(this.pendingTimer)
      this.pendingTimer = null
    }
  }

  private reset(): void {
    this.stop()
    this.starting = null
    this.store.nodes = {}
    this.store.version++
    this.store.selected = null
    this.store.pending = []
    this.keys.clear()
    this.writing.clear()
    this.refreshing.clear()
  }

  private async load(userUuid: string): Promise<void> {
    const rows = await readLibreIndex(userUuid)
    const nodes: Record<string, ILibreNode> = {}
    rows.forEach((row) => { nodes[row.uuid] = this.nodeOf(row) })
    this.store.nodes = nodes
    this.store.version++
  }

  private nodeOf(row: ILibreIndexRow): ILibreNode {
    if (!row.readable || row.place === null || row.data === null) {
      return LibreNode.unreadable(row.uuid, row.isRemoved, row.updatedAt)
    }
    return new LibreNode(row.uuid, row.place, row.data, row.isRemoved, row.updatedAt)
  }

  private onOperation(operation: IOperation): void {
    this.refresh(operation.record)
  }

  private async refresh(uuid: string): Promise<void> {
    const number = (this.refreshing.get(uuid) ?? 0) + 1
    this.refreshing.set(uuid, number)
    const operations = await sys().sync.getRecordOperations(EnumLibreSet.NODES, uuid)
    if (operations.length === 0 || !this.hasUserKey()) {
      return
    }
    const state = this.foldNode.exec(operations)
    if (state.key === null) {
      return
    }
    let row: ILibreIndexRow
    try {
      const plain = await this.decryptNode.exec(state, await this.nodeKey(uuid, state.key))
      if (this.refreshing.get(uuid) !== number) {
        return
      }
      row = { uuid, place: plain.place, data: plain.data, isRemoved: plain.isRemoved, updatedAt: plain.updatedAt, readable: true }
    } catch (e) {
      if (!this.isDecryptError(e)) {
        throw e
      }
      if (this.refreshing.get(uuid) !== number) {
        return
      }
      row = { uuid, place: null, data: null, isRemoved: state.isRemoved, updatedAt: state.updatedAt, readable: false }
    }
    this.store.nodes = { ...this.store.nodes, [uuid]: this.nodeOf(row) }
    this.store.version++
    await writeLibreIndexRow(this.userUuid, row)
  }

  /** Чужой ключ или испорченный шифротекст: узел виден как нечитаемый */
  private isDecryptError(error: unknown): boolean {
    return error instanceof Error && (error.message === EnumLibreError.CANNOT_DECRYPT || error.message === EnumUserKeyError.WRONG_KEY)
  }

  /** Ключ узла из памяти модуля; иначе unwrap обёрнутого ключа из события created */
  private async nodeKey(uuid: string, wrapped?: string): Promise<CryptoKey> {
    const cached = this.keys.get(uuid)
    if (cached !== undefined) {
      return cached
    }
    const key = wrapped ?? this.foldNode.exec(await sys().sync.getRecordOperations(EnumLibreSet.NODES, uuid)).key
    if (key === null) {
      throw new Error(EnumLibreError.NODE_NOT_FOUND)
    }
    const unwrapped = await this.requireUserKey().unwrap(key)
    this.keys.set(uuid, unwrapped)
    return unwrapped
  }

  private async encrypt(uuid: string, part: EnumLibrePart, text: string): Promise<string> {
    return this.cipher.encrypt(await this.nodeKey(uuid), part, text)
  }

  private requireUserKey(): IUserKey {
    const userKey = sys().auth.getUserKey()
    if (userKey === null) {
      throw new Error(EnumLibreError.NO_USER_KEY)
    }
    return userKey
  }

  private requireNode(uuid: string): ILibreNode {
    const node = this.store.nodes[uuid]
    if (node === undefined || !node.isReadable()) {
      throw new Error(EnumLibreError.NODE_NOT_FOUND)
    }
    return node
  }

  private checkLimit(data: ILibreData): void {
    if (new TextEncoder().encode(JSON.stringify(data)).length > NODE_LIMIT_BYTES) {
      throw new Error(EnumLibreError.NODE_TOO_LARGE)
    }
  }

  private writePlace(uuid: string, place: ILibrePlace): void {
    this.markPending(uuid)
    this.writeNode(uuid, EnumLibreEvent.MOVED, async () => ({ place: await this.encrypt(uuid, EnumLibrePart.PLACE, JSON.stringify(place)) }))
  }

  /** Событие узла после всех его событий, вызванных раньше; ошибка — в консоль, события нет */
  private writeNode(uuid: string, event: EnumLibreEvent, fields: () => Promise<Record<string, unknown>>): void {
    this.writesInFlight++
    const next = (this.writing.get(uuid) ?? Promise.resolve())
      .then(async () => sys().sync.write({ set: EnumLibreSet.NODES, record: uuid, kind: EnumOperationKind.EVENT, event, fields: await fields() }))
      .catch((e) => SysConsoleProviderModule().log(`Libre ${uuid}: event ${event} is not written: ${e}`, EnumConsoleType.ERROR))
      .finally(() => { this.writesInFlight-- })
    this.writing.set(uuid, next)
  }

  private markPending(uuid: string): void {
    if (!this.store.pending.includes(uuid)) {
      this.store.pending = [...this.store.pending, uuid]
    }
  }

  private checkPending(): void {
    if (this.store.pending.length > 0 && this.writesInFlight === 0 && sys().sync.getQueueSize() === 0) {
      this.store.pending = []
    }
  }
}

export default function (): LibreModuleV1 {
  return LibreModuleV1.getInstance()
}
